import React, { useState, useEffect, useCallback } from 'react';
import { cashBanksApi } from '../../utils/cashBanksApi';
import { entitiesApi } from '../../utils/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './CashBanks.css';

const today = () => new Date().toISOString().slice(0, 10);

const fmt = (val) =>
  val != null
    ? Number(val).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

export default function DailyReportPage() {
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [reportDate, setReportDate] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    entitiesApi.list().then(setEntities).catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { date: reportDate };
      if (selectedEntity) params.entityId = selectedEntity;
      const data = await cashBanksApi.dailyReport(params);
      setReport(data);
    } catch (e) {
      setError(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [reportDate, selectedEntity]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const totalBalance = report?.rows?.reduce((s, r) => s + Number(r.balance ?? 0), 0) ?? 0;
  const closedCount  = report?.rows?.filter((r) => r.isClosed).length ?? 0;

  return (
    <div className="cb-page">
      <div className="cb-header">
        <div className="cb-header-text">
          <h1 className="cb-title"><i className="fas fa-chart-line" style={{ marginRight: 8, color: 'var(--color-primary)' }} />Daily Cash & Banks Report</h1>
          <p className="cb-subtitle">Snapshot of all cash and bank account balances for a given date</p>
        </div>
        <button className="cb-btn cb-btn--secondary" onClick={() => window.print()}>
          <i className="fas fa-print" /> Print
        </button>
      </div>

      <div className="cb-toolbar">
        <label className="cb-label" style={{ margin: 0 }}>Date:</label>
        <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} max={today()} />
        <label className="cb-label" style={{ margin: 0 }}>Sub-Entity:</label>
        <select value={selectedEntity} onChange={(e) => setSelectedEntity(e.target.value)}>
          <option value="">All Entities</option>
          {entities.map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />{error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner variant="default" />
      ) : report ? (
        <>
          <div className="cb-report-grid">
            <div className="cb-report-card">
              <div className="cb-report-card__label">Total Balance (EGP)</div>
              <div className="cb-report-card__value">{fmt(totalBalance)}</div>
            </div>
            <div className="cb-report-card">
              <div className="cb-report-card__label">Accounts</div>
              <div className="cb-report-card__value">{report.rows?.length ?? 0}</div>
            </div>
            <div className="cb-report-card">
              <div className="cb-report-card__label">Closed Accounts</div>
              <div className="cb-report-card__value">{closedCount} / {report.rows?.length ?? 0}</div>
            </div>
          </div>

          <div className="cb-card">
            <div className="cb-card-header">
              <h2 className="cb-card-title">Account Balances — {reportDate}</h2>
            </div>
            <div className="cb-table-wrap">
              <table className="cb-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th>Type</th>
                    <th>Entity</th>
                    <th className="right">Balance (Local)</th>
                    <th>Closing Status</th>
                    <th className="right">Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows?.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No cash or bank accounts found</td></tr>
                  ) : (
                    report.rows?.map((row) => (
                      <tr key={row.account.id}>
                        <td className="mono">{row.account.code}</td>
                        <td>{row.account.name}</td>
                        <td>
                          <span className={`cb-badge cb-badge--${row.account.isBank ? 'bank' : 'cash'}`}>
                            <i className={`fas fa-${row.account.isBank ? 'university' : 'wallet'}`} />
                            {row.account.isBank ? 'Bank' : 'Cash'}
                          </span>
                        </td>
                        <td>{row.account.entity?.name ?? '—'}</td>
                        <td className="right" style={{ fontWeight: 600 }}>{fmt(row.balance)} {row.account.currency?.code}</td>
                        <td>
                          {row.isClosed ? (
                            <span className="cb-badge cb-badge--closed">
                              <i className="fas fa-lock" /> Step {row.closingStep} Closed
                            </span>
                          ) : (
                            <span className="cb-badge cb-badge--open">Open</span>
                          )}
                        </td>
                        <td className="right">{row.closingBalance != null ? fmt(row.closingBalance) : '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
